import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { IThunkRejectValue, RootState } from "../../../types";
import {
  AiApp,
  AiAppImage,
  AiAppPartner,
  AiAppsState,
} from "../../../types/ai";
import { getExtractErrors } from "../../../apis";
import { CustomErrorToast } from "../../../components/general/Toast";
import {
  getAiAppsApi,
  createAiAppApi,
  deleteAiAppApi,
  updateAiAppApi,
  stopAiAppApi,
  startAiAppApi,
  getAiAppPartnersApi,
  getAiAppImagesApi,
  aiAppSynchronizationApi,
  updateAiAppScalingApi,
  updateAiAppImageApi,
} from "../../../apis/ai-deploysAPI";

export const AiAppsRefreshStatus = [
  "DELETING",
  "INITIALIZING",
  "QUEUED",
  "SCALING",
  "STOPPING",
];

const initialState: AiAppsState = {
  apps: [],
  appsLoading: false,
  appsActionLoading: false, //for create, delete , update
  selectedApp: null, //for scaling
  scalingEditor: false,
  imageEditor: false,
  images: [],
  imagesLoading: false,
  partners: [],
  partnersLoading: false,
};

export const getAiAppsAsync = createAsyncThunk<
  { apps: Array<AiApp> },
  { withoutLoading?: boolean },
  IThunkRejectValue
>(
  "ai-apps",
  async (
    { withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiAppsAsync.pending(requestId, {
          withoutLoading,
        })
      );

      const response = await getAiAppsApi();
      const apps = response.data.Result;
      return fulfillWithValue({ apps });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

export const updateAiAppAsync = createAsyncThunk<
  any,
  { id: string; data: any },
  IThunkRejectValue
>("ai-apps/update", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateAiAppApi(id, data);
    return response;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const createAiAppAsync = createAsyncThunk<
  true,
  { data: any },
  IThunkRejectValue
>("ai-apps/create", async ({ data }, { rejectWithValue }) => {
  try {
    await createAiAppApi(data);
    return true;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const deleteAiAppAsync = createAsyncThunk<
  true,
  { appId: string },
  IThunkRejectValue
>("ai-apps/delete", async ({ appId }, { rejectWithValue }) => {
  try {
    await deleteAiAppApi(appId);
    return true;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const stopAiAppAsync = createAsyncThunk<
  any,
  { id: string },
  IThunkRejectValue
>("ai-apps/stop", async ({ id }, { rejectWithValue }) => {
  try {
    const response = await stopAiAppApi(id);
    return response;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const getAiAppImagesAsync = createAsyncThunk<
  { images: Array<AiAppImage> },
  { region: string; withoutLoading?: boolean },
  IThunkRejectValue
>(
  "ai-apps/images",
  async (
    { region, withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiAppImagesAsync.pending(requestId, {
          withoutLoading,
          region,
        })
      );

      const response = await getAiAppImagesApi(region);
      const images = response.data.Result;
      return fulfillWithValue({ images });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

export const getAiAppPartnersAsync = createAsyncThunk<
  { partners: Array<AiAppPartner> },
  { region: string; withoutLoading?: boolean },
  IThunkRejectValue
>(
  "ai-apps/partners",
  async (
    { region, withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiAppPartnersAsync.pending(requestId, {
          withoutLoading,
          region,
        })
      );

      const response = await getAiAppPartnersApi(region);
      const partners = response.data.Result;
      return fulfillWithValue({ partners });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

export const startAiAppAsync = createAsyncThunk<
  any,
  { id: string },
  IThunkRejectValue
>("ai-apps/start", async ({ id }, { rejectWithValue }) => {
  try {
    const response = await startAiAppApi(id);
    return response;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const aiAppSynchronizationAsync = createAsyncThunk<
  true,
  { id: string; data: any },
  IThunkRejectValue
>("ai-apps/datasync", async ({ id, data }, { rejectWithValue }) => {
  try {
    await aiAppSynchronizationApi(id, data);
    return true;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const updateAiAppScalingAsync = createAsyncThunk<
  any,
  { id: string; data: any },
  IThunkRejectValue
>("ai-apps/update/scaling", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateAiAppScalingApi(id, data);
    return response;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const updateAiAppImageAsync = createAsyncThunk<
  any,
  { id: string; data: any },
  IThunkRejectValue
>("ai-apps/update/image", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateAiAppImageApi(id, data);
    return response;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const aiAppsSlice = createSlice({
  name: "ai-apps",
  initialState,
  reducers: {
    handleSelectApp: (state, action: PayloadAction<AiApp>) => {
      state.selectedApp = action.payload;
    },
    handleToggleAiAppScalingModal: (
      state,
      action: PayloadAction<{ app: AiApp | null; isOpen: boolean }>
    ) => {
      state.selectedApp = action.payload.app;
      state.scalingEditor = action.payload.isOpen;
    },
    handleToggleAiAppImageModal: (
      state,
      action: PayloadAction<{ app: AiApp | null; isOpen: boolean }>
    ) => {
      state.selectedApp = action.payload.app;
      state.imageEditor = action.payload.isOpen;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAiAppsAsync.pending, (state, action) => {
        if (!action.meta.arg.withoutLoading) state.appsLoading = true;
      })
      .addCase(getAiAppsAsync.fulfilled, (state, action) => {
        const { apps } = action.payload;
        state.apps = apps;
        state.appsLoading = false;
      })
      .addCase(getAiAppsAsync.rejected, (state, { payload }) => {
        state.appsLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(updateAiAppAsync.pending, (state) => {
        state.appsActionLoading = true;
      })
      .addCase(updateAiAppAsync.fulfilled, (state) => {
        state.appsActionLoading = false;
      })
      .addCase(updateAiAppAsync.rejected, (state, { payload }) => {
        state.appsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(createAiAppAsync.pending, (state) => {
        state.appsActionLoading = true;
      })
      .addCase(createAiAppAsync.fulfilled, (state) => {
        state.appsActionLoading = false;
      })
      .addCase(createAiAppAsync.rejected, (state, { payload }) => {
        state.appsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(deleteAiAppAsync.pending, (state) => {
        state.appsActionLoading = true;
      })
      .addCase(deleteAiAppAsync.fulfilled, (state) => {
        state.appsActionLoading = false;
      })
      .addCase(deleteAiAppAsync.rejected, (state, { payload }) => {
        state.appsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(stopAiAppAsync.pending, (state) => {
        state.appsActionLoading = true;
      })
      .addCase(stopAiAppAsync.fulfilled, (state) => {
        state.appsActionLoading = false;
      })
      .addCase(stopAiAppAsync.rejected, (state, { payload }) => {
        state.appsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(getAiAppImagesAsync.pending, (state) => {
        state.imagesLoading = true;
      })
      .addCase(getAiAppImagesAsync.fulfilled, (state, action) => {
        state.imagesLoading = false;
        state.images = action.payload.images;
      })
      .addCase(getAiAppImagesAsync.rejected, (state, { payload }) => {
        state.imagesLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(getAiAppPartnersAsync.pending, (state) => {
        state.partnersLoading = true;
      })
      .addCase(getAiAppPartnersAsync.fulfilled, (state, action) => {
        state.partnersLoading = false;
        state.partners = action.payload.partners;
      })
      .addCase(getAiAppPartnersAsync.rejected, (state, { payload }) => {
        state.partnersLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(startAiAppAsync.pending, (state) => {
        state.appsActionLoading = true;
      })
      .addCase(startAiAppAsync.fulfilled, (state) => {
        state.appsActionLoading = false;
      })
      .addCase(startAiAppAsync.rejected, (state, { payload }) => {
        state.appsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(aiAppSynchronizationAsync.pending, (state) => {
        state.appsActionLoading = true;
      })
      .addCase(aiAppSynchronizationAsync.fulfilled, (state) => {
        state.appsActionLoading = false;
      })
      .addCase(aiAppSynchronizationAsync.rejected, (state, { payload }) => {
        state.appsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(updateAiAppScalingAsync.pending, (state) => {
        state.appsActionLoading = true;
      })
      .addCase(updateAiAppScalingAsync.fulfilled, (state) => {
        state.appsActionLoading = false;
      })
      .addCase(updateAiAppScalingAsync.rejected, (state, { payload }) => {
        state.appsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(updateAiAppImageAsync.pending, (state) => {
        state.appsActionLoading = true;
      })
      .addCase(updateAiAppImageAsync.fulfilled, (state) => {
        state.appsActionLoading = false;
      })
      .addCase(updateAiAppImageAsync.rejected, (state, { payload }) => {
        state.appsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });
  },
});

export const selectAiApps = (state: RootState) => state.aiApps.apps;
export const selectAiappsLoading = (state: RootState) =>
  state.aiApps.appsLoading;

export const selectAiAppsActionLoading = (state: RootState) =>
  state.aiApps.appsActionLoading;

export const selectSelectedAiApp = (state: RootState) =>
  state.aiApps.selectedApp;

export const selectAiAppScalingEditor = (state: RootState) =>
  state.aiApps.scalingEditor;

export const selectAiAppImageEditor = (state: RootState) =>
  state.aiApps.imageEditor;

export const selectAiAppImages = (state: RootState) => state.aiApps.images;
export const selectAiAppImagesLoading = (state: RootState) =>
  state.aiApps.imagesLoading;

export const selectAiAppPartners = (state: RootState) => state.aiApps.partners;
export const selectAiAppPartnersLoading = (state: RootState) =>
  state.aiApps.partnersLoading;

export const {
  handleSelectApp,
  handleToggleAiAppScalingModal,
  handleToggleAiAppImageModal,
} = aiAppsSlice.actions;
export default aiAppsSlice.reducer;
